import { Sun, Flame, Sparkles, Heart, Music, Crown, Flower2, UtensilsCrossed, HeartHandshake, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";

const MAPS_LINK = "https://maps.app.goo.gl/3mPQ61arj71XA8XU9?g_st=ac";

const EVENTS = [
    { id: "lagan", icon: Sun, day: "14", name: "Lagan (Sava)", tag: "The auspicious beginning", time: "11:15 AM", desc: "A warm, auspicious beginning to the wedding celebrations and traditional rituals." },
    { id: "ghritpaan", icon: Flame, day: "16", name: "Ghritpaan", tag: "Ritual of blessings", time: "4:15 PM", desc: "Sacred family ritual bestowing health, prosperity, and blessings." },
    { id: "vinayak", icon: Sparkles, day: "20", name: "Vinayak Sthapna", tag: "Invocation of Lord Ganesha", time: "6:15 PM", desc: "Invocation of Lord Ganesha to bless all forthcoming wedding ceremonies." },
    { id: "haldi", icon: Heart, day: "21", name: "Haldi Ceremony", tag: "Turmeric & laughter", time: "4:00 PM", desc: "A vibrant evening infused with turmeric, music, laughter, and family warmth." },
    { id: "mehandi", icon: Music, day: "22", name: "Mehandi", tag: "Henna & folk songs", time: "2:00 PM", desc: "An afternoon of intricate henna art, Rajasthani folk songs, and joyful dancing." },
    { id: "bandoli", icon: Crown, day: "23", name: "Badi Bandoli", tag: "The grand family night", time: "8:15 PM", desc: "A grand night of celebration, colour, and festive dining before the big day." },
    { id: "baraat", icon: Flower2, day: "24", name: "Baraat Arrival", tag: "The grand procession", time: "12:00 PM", desc: "Dileep's baraat arrives from Bera Lakhelav to Bera Hambro Ki Bawri." },
    { id: "preetibhoj", icon: UtensilsCrossed, day: "24", name: "Preetibhoj", tag: "The wedding feast", time: "11:15 AM", desc: "A feast of love and flavours shared with family and friends." },
];

const FINALE = {
    id: "panigrahana",
    icon: HeartHandshake,
    day: "24",
    name: "Panigrahana",
    tag: "The sacred vows",
    time: "3:15 PM",
    desc: "The sacred pheras and panigrahana. Two souls, one promise, blessed by both families.",
};

const Card = ({ event, index, finale = false }) => {
    const Icon = event.icon;
    return (
        <Reveal delay={0.08 * (index % 3)} className={finale ? "md:col-span-2 lg:col-span-3" : ""}>
            <article
                data-testid={`event-card-${event.id}`}
                className="group relative h-full rounded-3xl p-7 transition-transform duration-500 hover:-translate-y-2 sm:p-8"
                style={{
                    background: finale
                        ? "linear-gradient(135deg, rgba(138,59,78,0.96), rgba(112,44,61,0.98))"
                        : "rgba(255,253,250,0.85)",
                    border: "1px solid rgba(212,175,55,0.4)",
                    boxShadow: "0 24px 50px -30px rgba(138,59,78,0.35)",
                }}
            >
                <div className="flex items-start justify-between">
                    <div>
                        <span
                            className="font-display block text-5xl leading-none sm:text-6xl"
                            style={{ color: finale ? "var(--gold)" : "var(--wine)" }}
                        >
                            {event.day}
                        </span>
                        <span
                            className="mt-1 block text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
                            style={{ color: finale ? "var(--rose)" : "var(--gold)" }}
                        >
                            Nov 2026
                        </span>
                        {event.time && (
                            <span
                                className="mt-2 inline-block rounded-full px-3 py-1 text-[0.65rem] font-semibold tracking-[0.15em]"
                                style={{
                                    background: finale ? "rgba(212,175,55,0.18)" : "var(--blush)",
                                    color: finale ? "var(--gold)" : "var(--wine)",
                                    border: "1px solid rgba(212,175,55,0.45)",
                                }}
                            >
                                {event.time}
                            </span>
                        )}
                    </div>
                    <span
                        className="flex h-11 w-11 items-center justify-center rounded-full transition-transform duration-500 group-hover:rotate-12"
                        style={{
                            background: finale ? "rgba(212,175,55,0.2)" : "var(--blush)",
                            color: finale ? "var(--gold)" : "var(--wine)",
                            border: "1px solid rgba(212,175,55,0.45)",
                        }}
                    >
                        <Icon size={18} />
                    </span>
                </div>
                <h3
                    className="font-display mt-6 text-2xl tracking-tight"
                    style={{ color: finale ? "var(--ivory)" : "var(--ink)" }}
                >
                    {event.name}
                </h3>
                <p
                    className="mt-1 text-xs font-semibold uppercase tracking-[0.2em]"
                    style={{ color: finale ? "var(--rose)" : "var(--gold)" }}
                >
                    {event.tag}
                </p>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: finale ? "rgba(250,246,240,0.8)" : "var(--muted)" }}>
                    {event.desc}
                </p>
            </article>
        </Reveal>
    );
};

export default function EventSchedule() {
    return (
        <section id="events" data-testid="events-section" className="mx-auto max-w-5xl px-6 py-20 sm:py-28">
            <Reveal className="text-center">
                <p className="eyebrow">02 · The celebrations</p>
                <h2 className="font-display mt-3 text-3xl tracking-tight sm:text-4xl lg:text-5xl" style={{ color: "var(--ink)" }}>
                    Nine moments, <span className="italic" style={{ color: "var(--wine)" }}>one shared joy</span>
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm sm:text-base" style={{ color: "var(--muted)" }}>
                    Every ceremony is a chapter in our story. Come for one, stay for all.
                </p>
                <a
                    data-testid="events-venue-link"
                    href={MAPS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-transform duration-300 hover:-translate-y-0.5"
                    style={{ background: "var(--blush)", color: "var(--wine)", border: "1px solid rgba(212,175,55,0.45)" }}
                >
                    <MapPin size={13} /> All ceremonies · Bera Hambro Ki Bawri
                </a>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
                {EVENTS.map((e, i) => (
                    <Card key={e.id} event={e} index={i} />
                ))}
                <Card event={FINALE} index={0} finale />
            </div>
        </section>
    );
}
